import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import 'mapbox-gl/dist/mapbox-gl.css';
import DeckGL from '@deck.gl/react/typed';
import { StaticMap } from 'react-map-gl';
import { useSize } from 'react-hook-size';
import { getIsPOIsIncluded, getTouchedPoints } from 'store/sharedCampaignPlan/selectors';
import { DEFAULT_MAP_STYLE } from 'components/common/types/Planner.types';
import ZoomControls from 'components/common/ZoomControls/ZoomControls';
import Tooltip, { TooltipDirection, TooltipSize } from 'lib/Tooltip';
import Button, { Color } from 'lib/Button';
import poisSvg from 'assets/icons/pois.svg';
import { usePlannerMapLayers } from 'components/pages/Planner/PlannerMap/usePlannerMapLayers';
import { StreetView } from 'components/pages/Planner/PlannerMap/StreetView/StreetView';
import { useNearbyAssetsMarkers } from 'components/pages/Planner/PlannerMap/StreetView/useNearbyAssetsMarkers';
import PlannerMapTooltip from 'components/pages/Planner/PlannerMap/PlannerMapTooltip';
import CampaignPlanSearchBar from './CampaignPlanSearchBar/CampaignPlanSearchBar';
import { useCampaignPlanFurniture } from './useCampaignPlanFurniture';
import CampaignPlanSingleFurnitureCard from './CampaignPlanSingleFurnitureCard';
import CampaignPlanMultiFurnitureCard from './CampaignPlanMultiFurnitureCard';
import { useCampaignPlanMapState } from './useCampaignPlanMapState';

interface CampaignPlanMapProps {
  localeCode: string;
  currencyCode: string;
}

const CampaignPlanMap: React.FC<CampaignPlanMapProps> = ({ localeCode, currencyCode }) => {
  const mapContainerRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const containerSize = useSize(mapContainerRef);

  const isPOIsIncluded = useSelector(getIsPOIsIncluded);
  const hasTouchedPoints = useSelector(getTouchedPoints).length > 0;
  const [isPoiMarkersVisible, setIsPoiMarkersVisible] = useState(true);
  const showPoiToggle = isPOIsIncluded && hasTouchedPoints;

  const { mapLayers, clusterAssetsData } = usePlannerMapLayers({ isPoiMarkersVisible });
  const { furnitures, onCloseFurnitureCard } = useCampaignPlanFurniture({ localeCode, currencyCode });
  const [centre, setCentre] = useState<{ lat: number; lng: number } | null>(null);
  const [panoramaPosition, setPanoramaPosition] = useState<{ lat: number; lng: number } | null>(null);

  const {
    viewState,
    deckRef,
    cardPosition,
    hoveredFurnitureTooltip,
    furnitureTooltipPosition,
    updateMapZoom,
    onViewStateChange,
    onMapClick,
    onMapHover,
    getMapCursor,
    setBoundingBox,
    handleAssetSearchSelect,
    handleDeckAfterRender,
  } = useCampaignPlanMapState(containerSize);

  const nearbyAssetsMarkers = useNearbyAssetsMarkers(panoramaPosition ?? centre, clusterAssetsData);

  const streetView = useMemo(() => {
    if (!centre) return null;

    return (
      <StreetView
        center={centre}
        markers={nearbyAssetsMarkers}
        onPositionChange={setPanoramaPosition}
        close={() => {
          setCentre(null);
          setPanoramaPosition(null);
        }}
      />
    );
  }, [centre, nearbyAssetsMarkers]);

  return (
    <div className="relative w-full h-full overflow-hidden" ref={mapContainerRef}>
      <div className="absolute bottom-3 z-10 left-6 flex flex-col items-center gap-2">
        {showPoiToggle && (
          <div className="w-12 p-1 rounded-md shadow-sm border border-neutral-300 bg-white flex justify-center">
            <Tooltip
              tooltip={isPoiMarkersVisible ? 'Hide POI' : 'Show POI'}
              contentSize={TooltipSize.MAX}
              direction={TooltipDirection.RIGHT}
            >
              <Button
                dataTestId="enable-poi-button"
                svg={poisSvg}
                color={isPoiMarkersVisible ? Color.GREY : Color.TRANSPARENT}
                classNames="w-10 h-8"
                svgClassnames="text-neutral-900"
                ariaLabel={isPoiMarkersVisible ? 'Hide POI markers on map' : 'Show POI markers on map'}
                onClick={() => setIsPoiMarkersVisible((prev) => !prev)}
              />
            </Tooltip>
          </div>
        )}
        <ZoomControls zoom={viewState.zoom ?? 10} updateMapZoom={updateMapZoom} />
      </div>

      <div className="absolute top-3 left-1 z-10 px-6">
        <div className="flex items-center w-[250px] h-[46px] bg-white rounded-md px-1 shadow-sm">
          <CampaignPlanSearchBar setBoundingBox={setBoundingBox} onAssetSelect={handleAssetSearchSelect} />
        </div>
      </div>
      <DeckGL
        ref={deckRef}
        viewState={viewState}
        controller={{ dragRotate: false }}
        layers={mapLayers}
        onViewStateChange={onViewStateChange}
        onClick={onMapClick}
        getCursor={getMapCursor}
        onHover={onMapHover}
        onAfterRender={handleDeckAfterRender}
      >
        <StaticMap mapStyle={DEFAULT_MAP_STYLE} />
      </DeckGL>
      {streetView}
      {furnitures.length === 1 && (
        <CampaignPlanSingleFurnitureCard
          furniture={furnitures[0]}
          cardPosition={cardPosition}
          onClose={onCloseFurnitureCard}
          openStreetView={setCentre}
        />
      )}
      {furnitures.length > 1 && (
        <CampaignPlanMultiFurnitureCard
          furnitures={furnitures}
          cardPosition={cardPosition}
          onClose={onCloseFurnitureCard}
          openStreetView={setCentre}
        />
      )}
      {hoveredFurnitureTooltip && furnitureTooltipPosition && (
        <PlannerMapTooltip values={hoveredFurnitureTooltip.values} position={furnitureTooltipPosition} />
      )}
    </div>
  );
};

export default CampaignPlanMap;
